/**
 * Configuration Option API - Dataverse Integration
 */

import { MiddlewareAPI } from "@/lib/axios";
import { ICommonListApiRes, NetworkMethodEnum } from "@/types";
import { fetcher } from "@/utils/api/dataverse/fetcher";
import { mutator } from "@/utils/api/dataverse/mutator";
import { IConfigOption, IConfigOptionForm } from "../types/configOption";

export const URL = {
  configOptions: "/Dogma_ConfigurationOptionsApi",
  configOptionImage: "/ConfigurationOptionImageApi",
};

/**
 * Get all configuration options from Dataverse
 */
export const getAllConfigOptions = async (): Promise<IConfigOption[]> => {
  const response: ICommonListApiRes<IConfigOption> = await fetcher(
    `${URL.configOptions}?$orderby=createdon desc`
  );
  return response.value || [];
};

/**
 * Get configuration options by category ID from Dataverse
 */
export const getConfigOptionListByCategory = async (
  categoryId: string
): Promise<IConfigOption[]> => {
  const response: ICommonListApiRes<IConfigOption> = await fetcher(
    `${URL.configOptions}?$filter=_dogma_category_value eq '${categoryId}'&$orderby=createdon desc`
  );
  return response.value || [];
};

/**
 * Get single configuration option by ID from Dataverse
 */
export const getConfigOptionDetail = async (
  id: string
): Promise<IConfigOption> => {
  return await fetcher(`${URL.configOptions}(${id})`);
};

/**
 * Create new configuration option in Dataverse
 */
export const createConfigOption = async (
  categoryId: string,
  data: IConfigOptionForm
): Promise<IConfigOption> => {
  const payload = {
    ...data,
    // "dogma_category@odata.bind": `/dogma_categories(${categoryId})`,
    "dogma_Category@odata.bind": `/dogma_categories(${categoryId})`,
  };
  return await mutator(NetworkMethodEnum.POST, URL.configOptions, payload);
};

/**
 * Update existing configuration option in Dataverse
 */
export const updateConfigOption = async (data: {
  id: string;
  payload: Partial<IConfigOptionForm>;
}): Promise<IConfigOption> => {
  const updatePayload = {
    id: data.id,
    ...data.payload,
  };
  return await mutator(
    NetworkMethodEnum.PATCH,
    URL.configOptions,
    updatePayload
  );
};

/**
 * Delete configuration option from Dataverse
 */
export const deleteConfigOption = async (id: string): Promise<void> => {
  await MiddlewareAPI.delete(URL.configOptions, {
    data: { id },
  });
};

/**
 * Upload configuration option image to Azure
 */
export const uploadConfigOptionImageApi = async (
  imageData: File | Blob | ArrayBuffer,
  optionGuid: string,
  fileName: string,
  contentType: string = "image/png"
): Promise<any> => {
  // Convert to ArrayBuffer to send raw binary bytes without any transformation
  const buffer =
    imageData instanceof ArrayBuffer
      ? imageData
      : await (imageData as Blob).arrayBuffer();

  const response = await MiddlewareAPI.post(
    `${URL.configOptionImage}?guid=${optionGuid}&fileName=${fileName}`,
    buffer,
    {
      headers: {
        "Content-Type": contentType,
        // Remove OData annotation header — not applicable for binary uploads
        "prefer": "",
      },
    }
  );
  return response.data;
};
